import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import {
  BotMessageSquareIcon,
  ImageIcon,
  LoaderCircleIcon,
  MapIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { Button } from "@/components/catalyst/button";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { relocationImageGenerations } from "@/app/Relocations/GraphQL/relocationImageGenerations";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationImageGenerationDialog } from "@/app/Relocations/Components/Dialogs/RelocationImageGenerationDialog";
import { RelocationImageGenerationList } from "@/app/Relocations/Components/RelocationImageGenerationList";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { TripEditDialog } from "@/app/Cities/Trips/Components/TripEditDialog";

const POLL_TIMEOUT_SECONDS = 120;
const POLL_INTERVAL_MS = 3000;

export function RelocationImageGenerationScreen() {
  const relocation = useRelocationRecord();

  const { data, refetch } = useGqlQuery(relocationImageGenerations, {
    id: relocation.id,
  });

  const { open: openGenerationDialog } = useDialog(
    RelocationImageGenerationDialog,
  );
  const { open: openTripEdit } = useDialog(TripEditDialog);

  const [isGenerating, setIsGenerating] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationCountBeforeRef = useRef<number>(0);

  const clearTimers = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startPolling = () => {
    generationCountBeforeRef.current =
      data?.relocationImageGenerations?.length ?? 0;
    clearTimers();
    setHasTimedOut(false);
    setIsGenerating(true);
    setTimeRemaining(POLL_TIMEOUT_SECONDS);

    countdownIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => Math.max(prev - 1, 0));
    }, 1000);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await refetch();
        const newCount = result.data?.relocationImageGenerations?.length ?? 0;

        if (newCount > generationCountBeforeRef.current) {
          stopPolling();
          toast.success("Image generated successfully!");
        }
      } catch (error) {
        console.error("Error polling for image:", error);
      }
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(() => {
      stopPolling();
      setHasTimedOut(true);
      toast.error("Image generation timed out", {
        description:
          "No image was produced within 2 minutes. The job may still be running — refresh shortly, or try generating again.",
      });
    }, POLL_TIMEOUT_SECONDS * 1000);
  };

  const stopPolling = () => {
    setIsGenerating(false);
    setTimeRemaining(0);
    clearTimers();
  };

  const handleOpenGenerationDialog = () => {
    openGenerationDialog({
      relocationId: relocation.id,
      onGenerationStarted: () => {
        toast.success("Image generation started", {
          description: "This will take up to 2 minutes...",
        });
        startPolling();
      },
    });
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return (
    <div className="space-y-6 py-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Generated Image</h2>
        <Button
          LeadingIcon={BotMessageSquareIcon}
          onClick={handleOpenGenerationDialog}
          color="blue"
          disabled={isGenerating}
        >
          Generate Image
        </Button>
      </div>
      <RelocationImageGenerationList
        generatingPlaceholder={
          isGenerating ? (
            <div className="relative aspect-square overflow-hidden rounded-lg border-2 border-dashed border-blue-300 bg-blue-50">
              <div className="flex h-full flex-col items-center justify-center gap-3 p-4">
                <LoaderCircleIcon className="h-10 w-10 animate-spin text-blue-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-blue-600">
                    Generating...
                  </p>
                  <p className="text-xs text-blue-400">
                    {timeRemaining}s remaining
                  </p>
                </div>
              </div>
            </div>
          ) : hasTimedOut ? (
            <div className="relative aspect-square overflow-hidden rounded-lg border-2 border-dashed border-red-300 bg-red-50">
              <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
                <TriangleAlertIcon className="h-10 w-10 text-red-400" />
                <div>
                  <p className="text-sm font-medium text-red-600">
                    Generation timed out
                  </p>
                  <p className="text-xs text-red-400">
                    No image appeared within 2 minutes.
                  </p>
                </div>
                <Button color="red" onClick={handleOpenGenerationDialog}>
                  Try again
                </Button>
              </div>
            </div>
          ) : null
        }
      />

      {relocation.trip ? (
        <div>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Trip Image</h2>
            <div className="flex gap-2">
              <Button
                LeadingIcon={MapIcon}
                outline
                onClick={() => {
                  openTripEdit({ tripId: relocation.trip!.id });
                }}
              >
                Change Route
              </Button>
            </div>
          </div>
          {relocation.trip.square_image ? (
            <div className="mt-2 max-w-sm">
              <ImoovaImage
                alt="Trip Image"
                className="w-full rounded-lg border-2 border-gray-200 object-contain"
                media={relocation.trip.square_image}
              />
            </div>
          ) : (
            <div className="mt-2 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
              <ImageIcon className="mb-4 h-12 w-12 text-gray-400" />
              <p className="text-sm font-medium text-gray-900">No trip image</p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
